import { useMemo } from 'react'
import { Billboard, Text, Line } from '@react-three/drei'
import * as THREE from 'three'
import Planet from './Planet'
import { NoteNode, Edge, Vec3, colorForType, normalizeType } from './types'

interface GraphSceneProps {
  nodes: NoteNode[]
  edges: Edge[]
  selectedId: string | null
  highlightedIds: Set<string> | null
  activeTypes: Set<string>
  onSelect: (node: NoteNode) => void
  onFocus: (node: NoteNode, position: Vec3) => void
}

const LOD_THRESHOLD = 200

// Layout force-directed pre-computado (posiciones estáticas)
function computeLayout(nodes: NoteNode[], edges: Edge[]) {
  const positions = new Map<string, Vec3>()
  const radius = 22
  nodes.forEach((node, i) => {
    const phi = Math.acos(-1 + (2 * i) / Math.max(nodes.length, 1))
    const theta = Math.sqrt(nodes.length * Math.PI) * phi
    positions.set(node.id, [
      radius * Math.cos(theta) * Math.sin(phi),
      radius * Math.sin(theta) * Math.sin(phi),
      radius * Math.cos(phi),
    ])
  })

  const iterations = nodes.length > 400 ? 15 : 40
  for (let iter = 0; iter < iterations; iter++) {
    const forces = new Map<string, Vec3>()
    nodes.forEach((n) => forces.set(n.id, [0, 0, 0]))

    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = positions.get(nodes[i].id)!
        const b = positions.get(nodes[j].id)!
        const dx = a[0] - b[0]
        const dy = a[1] - b[1]
        const dz = a[2] - b[2]
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) + 0.1
        const force = 6 / (dist * dist)
        const fx = (dx / dist) * force
        const fy = (dy / dist) * force
        const fz = (dz / dist) * force
        const fa = forces.get(nodes[i].id)!
        const fb = forces.get(nodes[j].id)!
        fa[0] += fx
        fa[1] += fy
        fa[2] += fz
        fb[0] -= fx
        fb[1] -= fy
        fb[2] -= fz
      }
    }

    edges.forEach((edge) => {
      const a = positions.get(edge.source_note_id)
      const b = positions.get(edge.target_note_id)
      if (!a || !b) return
      const dx = b[0] - a[0]
      const dy = b[1] - a[1]
      const dz = b[2] - a[2]
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) + 0.1
      const force = dist * 0.012
      const fx = (dx / dist) * force
      const fy = (dy / dist) * force
      const fz = (dz / dist) * force
      const fa = forces.get(edge.source_note_id)!
      const fb = forces.get(edge.target_note_id)!
      fa[0] += fx
      fa[1] += fy
      fa[2] += fz
      fb[0] -= fx
      fb[1] -= fy
      fb[2] -= fz
    })

    nodes.forEach((node) => {
      const pos = positions.get(node.id)!
      const force = forces.get(node.id)!
      pos[0] += force[0] * 0.5
      pos[1] += force[1] * 0.5
      pos[2] += force[2] * 0.5
    })
  }

  return positions
}

// Nebulosa: clusters lejanos de baja conectividad como Points con glow
function NebulaPoints({
  nodes,
  positions,
}: {
  nodes: NoteNode[]
  positions: Map<string, Vec3>
}) {
  const { posArray, colArray } = useMemo(() => {
    const posArray = new Float32Array(nodes.length * 3)
    const colArray = new Float32Array(nodes.length * 3)
    nodes.forEach((n, i) => {
      const p = positions.get(n.id) || [0, 0, 0]
      posArray[i * 3] = p[0]
      posArray[i * 3 + 1] = p[1]
      posArray[i * 3 + 2] = p[2]
      const c = new THREE.Color(colorForType(n.note_type))
      colArray[i * 3] = c.r
      colArray[i * 3 + 1] = c.g
      colArray[i * 3 + 2] = c.b
    })
    return { posArray, colArray }
  }, [nodes, positions])

  const tex = useMemo(() => {
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
    g.addColorStop(0, 'rgba(255,255,255,0.9)')
    g.addColorStop(0.4, 'rgba(255,255,255,0.35)')
    g.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, size, size)
    return new THREE.CanvasTexture(canvas)
  }, [])

  if (nodes.length === 0) return null

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[posArray, 3]} />
        <bufferAttribute attach="attributes-color" args={[colArray, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={1.6}
        sizeAttenuation
        map={tex}
        vertexColors
        transparent
        opacity={0.7}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  )
}

export default function GraphScene({
  nodes,
  edges,
  selectedId,
  highlightedIds,
  activeTypes,
  onSelect,
  onFocus,
}: GraphSceneProps) {
  const positions = useMemo(() => computeLayout(nodes, edges), [nodes, edges])

  // Filtro por tipo
  const visibleNodes = useMemo(
    () => nodes.filter((n) => activeTypes.has(normalizeType(n.note_type))),
    [nodes, activeTypes],
  )
  // Vecinos directos del nodo seleccionado
  const neighborIds = useMemo(() => {
    if (!selectedId) return new Set<string>()
    const s = new Set<string>()
    edges.forEach((e) => {
      if (e.source_note_id === selectedId) s.add(e.target_note_id)
      if (e.target_note_id === selectedId) s.add(e.source_note_id)
    })
    return s
  }, [edges, selectedId])

  // Conjunto de nodos "brillantes"; el resto se atenúa
  const focusSet = useMemo<Set<string> | null>(() => {
    if (highlightedIds) return highlightedIds
    if (selectedId) {
      const s = new Set<string>(neighborIds)
      s.add(selectedId)
      return s
    }
    return null
  }, [highlightedIds, selectedId, neighborIds])

  // LOD: si hay demasiados nodos visibles, los de baja conectividad van a nebulosa
  const lodActive = visibleNodes.length > LOD_THRESHOLD
  const planetNodes = useMemo(
    () =>
      lodActive
        ? visibleNodes.filter((n) => n.connectivity_count >= 2)
        : visibleNodes,
    [lodActive, visibleNodes],
  )
  const nebulaNodes = useMemo(
    () =>
      lodActive
        ? visibleNodes.filter((n) => n.connectivity_count < 2)
        : [],
    [lodActive, visibleNodes],
  )
  const planetIds = useMemo(
    () => new Set(planetNodes.map((n) => n.id)),
    [planetNodes],
  )

  // Edges deduplicados entre nodos renderizados como planetas
  const renderEdges = useMemo(() => {
    const seen = new Set<string>()
    const out: Edge[] = []
    edges.forEach((e) => {
      if (!planetIds.has(e.source_note_id) || !planetIds.has(e.target_note_id))
        return
      const key =
        e.source_note_id < e.target_note_id
          ? `${e.source_note_id}|${e.target_note_id}`
          : `${e.target_note_id}|${e.source_note_id}`
      if (seen.has(key)) return
      seen.add(key)
      out.push(e)
    })
    return out
  }, [edges, planetIds])

  if (nodes.length === 0) {
    return (
      <Billboard>
        <Text fontSize={1} color="white">
          Aún no hay notas. ¡Empieza a añadir conocimiento!
        </Text>
      </Billboard>
    )
  }

  return (
    <group>
      {planetNodes.map((node) => {
        const pos = (positions.get(node.id) || [0, 0, 0]) as Vec3
        const dimmed = focusSet != null && !focusSet.has(node.id)
        return (
          <Planet
            key={node.id}
            node={node}
            position={pos}
            selected={selectedId === node.id}
            dimmed={dimmed}
            onSelect={onSelect}
            onFocus={onFocus}
          />
        )
      })}

      <NebulaPoints nodes={nebulaNodes} positions={positions} />

      {renderEdges.map((edge, i) => {
        const a = positions.get(edge.source_note_id)
        const b = positions.get(edge.target_note_id)
        if (!a || !b) return null
        const connectedToSel =
          selectedId != null &&
          (edge.source_note_id === selectedId ||
            edge.target_note_id === selectedId)
        const bothBright =
          focusSet == null ||
          (focusSet.has(edge.source_note_id) &&
            focusSet.has(edge.target_note_id))
        const dim = focusSet != null && !bothBright
        return (
          <Line
            key={`edge-${i}`}
            points={[a, b]}
            color={connectedToSel ? '#a5b4fc' : '#4c5aa8'}
            lineWidth={connectedToSel ? 2 : Math.max(edge.weight * 1.5, 0.4)}
            transparent
            opacity={dim ? 0.04 : connectedToSel ? 0.9 : Math.min(edge.weight, 0.5)}
          />
        )
      })}
    </group>
  )
}
